import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { photographerAPI } from '@services/photographerApi';
import type { PhotographerCustomer, CustomersContextType } from '@/types/photographer';

interface CustomersState {
  customers: PhotographerCustomer[];
  loading: boolean;
  error: string | null;
  filters: {
    search: string;
    status: string | null;
    sortBy: 'name' | 'totalSpent' | 'lastOrder' | 'created';
    sortOrder: 'asc' | 'desc';
  };
}

type CustomersAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: PhotographerCustomer[] }
  | { type: 'FETCH_FAILURE'; payload: string }
  | { type: 'SET_FILTERS'; payload: Partial<CustomersState['filters']> }
  | { type: 'CLEAR_ERROR' };

const initialState: CustomersState = {
  customers: [],
  loading: false,
  error: null,
  filters: {
    search: '',
    status: null,
    sortBy: 'created',
    sortOrder: 'desc',
  },
};

const customersReducer = (state: CustomersState, action: CustomersAction): CustomersState => {
  switch (action.type) {
    case 'FETCH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };

    case 'FETCH_SUCCESS':
      return {
        ...state,
        loading: false,
        customers: action.payload,
        error: null,
      };

    case 'FETCH_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case 'SET_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

const CustomersContext = createContext<CustomersContextType | null>(null);

interface CustomersProviderProps {
  children: ReactNode;
}

export const PhotographerCustomersProvider: React.FC<CustomersProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(customersReducer, initialState);

  const fetchCustomers = async (filters?: Partial<CustomersState['filters']>): Promise<void> => {
    dispatch({ type: 'FETCH_START' });

    try {
      const queryFilters = filters || state.filters;
      const response = await photographerAPI.getCustomers({
        search: queryFilters.search || undefined,
        status: queryFilters.status || undefined,
        sortBy: queryFilters.sortBy,
        sortOrder: queryFilters.sortOrder,
      });

      dispatch({
        type: 'FETCH_SUCCESS',
        payload: response.data,
      });
    } catch (error: any) {
      dispatch({
        type: 'FETCH_FAILURE',
        payload: error.message || 'Failed to fetch customers',
      });
      throw error;
    }
  };

  const exportCustomers = async (format: 'csv' | 'xlsx'): Promise<void> => {
    try {
      const blob = await photographerAPI.exportCustomers(format, state.filters);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `customers-export.${format}`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error: any) {
      dispatch({
        type: 'FETCH_FAILURE',
        payload: error.message || 'Failed to export customers',
      });
      throw error;
    }
  };

  const setFilters = (filters: Partial<CustomersState['filters']>): void => {
    dispatch({
      type: 'SET_FILTERS',
      payload: filters,
    });
  };

  const value: CustomersContextType = {
    ...state,
    fetchCustomers,
    exportCustomers,
    setFilters,
  };

  return <CustomersContext.Provider value={value}>{children}</CustomersContext.Provider>;
};

export const usePhotographerCustomers = (): CustomersContextType => {
  const context = useContext(CustomersContext);
  if (!context) {
    throw new Error('usePhotographerCustomers must be used within PhotographerCustomersProvider');
  }
  return context;
};
